import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { UnionJack } from "@/components/UnionJack";
import { subscribeNewsletter } from "@/lib/api";

const INSTAGRAM_IMAGES = [
    "/generated/friesian-cow.jpg",
    "/generated/farm-hartley-1.jpg",
    "/generated/product-ribeye.jpg",
    "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
    "/generated/fillet-cut.jpg",
    "/generated/farm-blackmore-1.jpg",
];

export const Footer = () => {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            const res = await subscribeNewsletter(email);
            toast.success(res.already_subscribed ? "You're already on the list." : "Welcome to British Steak Co.", {
                description: res.already_subscribed ? undefined : "Be first to hear about new cuts and limited editions.",
            });
            setEmail("");
        } catch {
            toast.error("Please enter a valid email address.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer className="bg-[#0B0B0C] text-[#F6F1E7] relative noise-overlay" data-testid="site-footer">
            {/* Instagram strip */}
            <div className="grid grid-cols-3 sm:grid-cols-6">
                {INSTAGRAM_IMAGES.map((src, i) => (
                    <div key={i} className="aspect-square overflow-hidden group">
                        <img
                            src={src}
                            alt="British Steak Co. on Instagram"
                            loading="lazy"
                            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.05] transition-all duration-700"
                        />
                    </div>
                ))}
            </div>

            <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Brand + newsletter */}
                    <div className="lg:col-span-5">
                        <div className="font-serif-display text-2xl font-semibold tracking-[0.24em]">
                            BRITISH STEAK CO.
                        </div>
                        <p className="mt-4 text-sm text-white/50 leading-relaxed max-w-sm">
                            Premium dry aged beef. Raised with care. Crafted with patience. Delivered to you.
                        </p>
                        <p className="mt-4 flex items-start gap-2.5 font-mono-meta text-[9px] tracking-[0.15em] text-white/35 leading-relaxed max-w-sm" data-testid="footer-provenance-note">
                            <UnionJack width={16} muted className="mt-0.5" />
                            <span>100% BRITISH BEEF FROM NAMED BRITISH FARMS. TRADITIONALLY PROCESSED — NEVER HALAL.</span>
                        </p>
                        <div className="mt-8">
                            <p className="eyebrow text-[#C9B37A] mb-3">Join the list</p>
                            <form onSubmit={handleSubscribe} className="flex max-w-sm" data-testid="newsletter-form">
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    className="flex-1 bg-white/[0.06] border border-white/15 px-4 py-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#C9B37A] transition-colors rounded-l-sm"
                                    data-testid="newsletter-email-input"
                                />
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-[#C9B37A] text-[#0B0B0C] px-5 hover:bg-[#d6c28e] transition-colors rounded-r-sm disabled:opacity-60"
                                    aria-label="Subscribe"
                                    data-testid="newsletter-submit-button"
                                >
                                    <ArrowRight size={16} />
                                </button>
                            </form>
                            <p className="font-mono-meta text-[9px] text-white/25 mt-3 tracking-wide">
                                BE FIRST TO HEAR ABOUT NEW CUTS, LIMITED EDITIONS AND OFFERS
                            </p>
                        </div>
                    </div>

                    {/* Links */}
                    <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-10">
                        <div>
                            <p className="eyebrow text-white/40 mb-4">Shop</p>
                            <ul className="space-y-3 text-sm text-white/65">
                                <li><Link to="/shop" className="hover:text-[#C9B37A] transition-colors" data-testid="footer-all-cuts-link">All Cuts</Link></li>
                                <li><Link to="/shop?category=gift" className="hover:text-[#C9B37A] transition-colors" data-testid="footer-gift-boxes-link">Gift Boxes</Link></li>
                                <li><Link to="/shop?category=bundle" className="hover:text-[#C9B37A] transition-colors">Bundles</Link></li>
                                <li><Link to="/shop?category=accessory" className="hover:text-[#C9B37A] transition-colors" data-testid="footer-accessories-link">Accessories</Link></li>
                                <li><Link to="/club" className="hover:text-[#C9B37A] transition-colors" data-testid="footer-club-link">The Beef Club</Link></li>
                            </ul>
                        </div>
                        <div>
                            <p className="eyebrow text-white/40 mb-4">Our Company</p>
                            <ul className="space-y-3 text-sm text-white/65">
                                <li><Link to="/our-story" className="hover:text-[#C9B37A] transition-colors" data-testid="footer-our-story-link">Our Story</Link></li>
                                <li><Link to="/farms" className="hover:text-[#C9B37A] transition-colors" data-testid="footer-farms-link">Meet The Farmers</Link></li>
                                <li><Link to="/our-story" className="hover:text-[#C9B37A] transition-colors">The Process</Link></li>
                                <li><Link to="/journal" className="hover:text-[#C9B37A] transition-colors" data-testid="footer-journal-link">Journal</Link></li>
                            </ul>
                        </div>
                        <div>
                            <p className="eyebrow text-white/40 mb-4">Help</p>
                            <ul className="space-y-3 text-sm text-white/65">
                                <li><span className="cursor-default">FAQs</span></li>
                                <li><span className="cursor-default">Delivery</span></li>
                                <li><span className="cursor-default">Wholesale &amp; Trade</span></li>
                                <li><span className="cursor-default">Contact</span></li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-white/[0.08] flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="font-mono-meta text-[10px] text-white/30 tracking-wide">
                        © {new Date().getFullYear()} BRITISH STEAK CO. ALL RIGHTS RESERVED.
                    </p>
                    <div className="flex items-center gap-6 text-white/40">
                        <span className="font-mono-meta text-[10px] tracking-wide">TERMS</span>
                        <span className="font-mono-meta text-[10px] tracking-wide">PRIVACY</span>
                        <Instagram size={16} className="hover:text-[#C9B37A] transition-colors cursor-pointer" />
                        <Facebook size={16} className="hover:text-[#C9B37A] transition-colors cursor-pointer" />
                    </div>
                </div>
            </div>
        </footer>
    );
};
